import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import { alpha } from "@mui/material/styles";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";

const toHours = (p) => (p.timeUnit === "days" ? p.time * 24 : p.time);

const windowLabel = (p) => {
  const unit = p.timeUnit === "days" ? (Number(p.time) === 1 ? "day" : "days") : Number(p.time) === 1 ? "hour" : "hours";
  return `${p.time} ${unit}`;
};

// Same semantics as the server (apointa-api policyHelpers): a location's override
// group replaces the club-wide bookings set; a location in no group inherits it.
const resolveBookingPolicies = (policies, locationPolicies, locationId) => {
  const clubDefault = (policies || []).filter((p) => p.isActive && p.type === "bookings");
  let applicable = clubDefault;
  if (locationId) {
    const group = (locationPolicies || []).find((g) => Array.isArray(g.locationIds) && g.locationIds.includes(locationId));
    const override = group ? (group.policies || []).filter((p) => p.isActive && p.type === "bookings") : [];
    if (override.length > 0) applicable = override;
  }
  return applicable.slice().sort((a, b) => toHours(a) - toHours(b));
};

// Turn the applicable policy windows into human copy. Windows are sorted
// nearest-first; the largest window is the free-cancellation boundary.
function CancellationCopy({ policies, locationPolicies, locationId }) {
  const applicable = resolveBookingPolicies(policies, locationPolicies, locationId);

  if (applicable.length === 0) {
    return <>Cancel any time before your booking starts and the full amount is credited to your wallet.</>;
  }

  const outermost = applicable[applicable.length - 1];
  const tierLine = (p) => (Number(p.value) >= 100 ? "no credit" : `${p.value}% cancellation fee`);

  if (applicable.length === 1) {
    return (
      <>
        Cancel up to {windowLabel(outermost)} before your booking and the full amount is credited to your wallet. Cancellations within{" "}
        {windowLabel(outermost)} {Number(outermost.value) >= 100 ? "receive no credit" : `are subject to a ${outermost.value}% cancellation fee`}.
      </>
    );
  }

  return (
    <>
      Cancel more than {windowLabel(outermost)} before your booking and the full amount is credited to your wallet. Later cancellations:{" "}
      {applicable.map((p, idx) => (
        <React.Fragment key={p._id || idx}>
          within {windowLabel(p)} — {tierLine(p)}
          {idx < applicable.length - 1 ? "; " : "."}
        </React.Fragment>
      ))}
    </>
  );
}

// `embedded` drops the standalone card so this can sit as a section inside the
// booking card (mobile), where the two belong to the same booking. `isCourt`
// titles the card to match the Terms & Conditions the client ticks below it.
// `clubRules` are the club's own rules for a court or event booking, typed in
// Partners — a single rule keeps the one-line look the court rule always had.
function CheckoutPoliciesPanel({ policies = [], locationPolicies = [], locationId = null, embedded = false, isCourt = false, clubRules = [] }) {
  const Wrapper = embedded ? Box : Paper;
  const wrapperProps = embedded
    ? { sx: { p: { xs: 2.5, md: 3 } } }
    : {
        elevation: 0,
        sx: {
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          border: 1,
          borderColor: (theme) => alpha(theme.palette.divider, 0.6),
          backgroundColor: "background.paper"
        }
      };

  return (
    <Wrapper {...wrapperProps}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
        <Box
          sx={{
            width: "2.25rem",
            height: "2.25rem",
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
            color: "primary.main"
          }}
        >
          <PolicyOutlinedIcon sx={{ fontSize: "1.25rem" }} />
        </Box>
        <Typography sx={{ fontSize: "1.125rem", fontWeight: 600, color: "text.primary" }}>
          {isCourt ? "Terms & Conditions" : "Booking Policies & Cancellation"}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "text.secondary",
              mb: 1
            }}
          >
            Cancellation
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.6 }}>
            <CancellationCopy policies={policies} locationPolicies={locationPolicies} locationId={locationId} />
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "text.secondary",
              mb: 1
            }}
          >
            No-Show Policy
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.6 }}>
            Bookings are held for 15 minutes past the start time. After this period, the court may be released and no credit is given.
          </Typography>
        </Grid>
      </Grid>

      {clubRules.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: clubRules.length === 1 ? "center" : "flex-start",
            gap: 1.25,
            mt: 2.5,
            px: 1.75,
            py: 1.5,
            borderRadius: 2,
            backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.08)
          }}
        >
          <BlockOutlinedIcon sx={{ fontSize: "1.125rem", color: "warning.main", flexShrink: 0, mt: clubRules.length === 1 ? 0 : 0.25 }} />
          {clubRules.length === 1 ? (
            <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary", lineHeight: 1.5 }}>
              {clubRules[0]}
            </Typography>
          ) : (
            <Box
              component="ul"
              sx={{ m: 0, pl: 2.25, listStyleType: "disc", "& > li": { display: "list-item" }, "& > li + li": { mt: 0.75 } }}
            >
              {clubRules.map((rule, i) => (
                <Typography key={i} component="li" variant="body2" sx={{ fontWeight: 600, color: "text.primary", lineHeight: 1.5 }}>
                  {rule}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Wrapper>
  );
}

export default CheckoutPoliciesPanel;
